import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MapCacheEntry {
  orderId: number;
  showMap: boolean;
  routeData?: google.maps.DirectionsResult;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class MapCacheService {
  private readonly CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

  private mapCacheSubject = new BehaviorSubject<Map<number, MapCacheEntry>>(new Map());
  public mapCache$: Observable<Map<number, MapCacheEntry>> = this.mapCacheSubject.asObservable();

  constructor() { }

  public wasMapLoaded(orderId: number): boolean {
    const cache = this.mapCacheSubject.getValue();
    const entry = cache.get(orderId);
    if (!entry) {
      return false;
    }

    if (this.isCacheExpired(entry)) {
      this.removeFromCache(orderId);
      return false;
    }

    return entry.showMap;
  }

  public setMapLoaded(orderId: number, showMap: boolean = true): void {
    const cache = this.mapCacheSubject.getValue();
    const existingEntry = cache.get(orderId);
    const entry: MapCacheEntry = {
      orderId,
      showMap,
      routeData: existingEntry?.routeData,
      timestamp: Date.now()
    };
    cache.set(orderId, entry);
    this.mapCacheSubject.next(cache);
  }

  public setRouteData(orderId: number, routeData: google.maps.DirectionsResult): void {
    const cache = this.mapCacheSubject.getValue();
    const existingEntry = cache.get(orderId);
    const entry: MapCacheEntry = {
      orderId,
      showMap: existingEntry?.showMap ?? true,
      routeData,
      timestamp: Date.now()
    };
    cache.set(orderId, entry);
    this.mapCacheSubject.next(cache);
  }

  public getRouteData(orderId: number): google.maps.DirectionsResult | undefined {
    const cache = this.mapCacheSubject.getValue();
    const entry = cache.get(orderId);
    if (!entry || this.isCacheExpired(entry)) {
      return undefined;
    }

    return entry.routeData;
  }

  public getCacheEntry(orderId: number): MapCacheEntry | undefined {
    const cache = this.mapCacheSubject.getValue();
    const entry = cache.get(orderId);
    if (entry && this.isCacheExpired(entry)) {
      this.removeFromCache(orderId);
      return undefined;
    }

    return entry;
  }

  public removeFromCache(orderId: number): void {
    const cache = this.mapCacheSubject.getValue();
    cache.delete(orderId);
    this.mapCacheSubject.next(cache);
  }

  public clearAllCache(): void {
    this.mapCacheSubject.next(new Map());
  }

  public cleanupExpiredEntries(): void {
    const cache = this.mapCacheSubject.getValue();
    cache.forEach((entry, orderId) => {
      if (this.isCacheExpired(entry)) {
        cache.delete(orderId);
      }
    });
    this.mapCacheSubject.next(cache);
  }

  private isCacheExpired(entry: MapCacheEntry): boolean {
    return Date.now() - entry.timestamp > this.CACHE_EXPIRY_MS;
  }
}
